import { useState, useEffect, useRef } from 'react';

import Loading from './Loading';
import GameBoard from './GameBoard';

import './Minesweeper.css';

export type Board = boolean[][];
export type Game = number[][];


export const LOST = 99;
export const FLAGGED = 89;

function Minesweeper() {
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState<Board>([[]]);
  const [game, setGame] = useState<Game>([[]]);
  const [lenX, setLenX] = useState<number>(16);
  const [lenY, setLenY] = useState<number>(10);
  const [bombs, setBombs] = useState<number>(0);
  const [hidden, setHidden] = useState<number>(16*10);
  const [flagged, setFlagged] = useState<number>(0);
  const [lost, setLost] = useState(false);
  const [won, setWon] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    if( window.innerWidth < 600) {
      setLenX(10);
      setLenY(7);
      setHidden(10 * 7);
      setNarrow(true);
    }
    else {
      setLenX(16);
      setLenY(10);
      setHidden(16 * 10);
    }

    // did not end up using this
    // @ts-ignore
    const touchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
    setIsTouch(touchDevice);
  }, []);

  useEffect(() => {
    setLoading(true);
    createNewBoard();
    setLoading(false);
  }, [lenX, lenY]);

  // update counts
  useEffect(() => {
    if(board.length === lenY && board[0] && board[0].length === lenX) {

      let _bombs = 0;
      let _hiddenSpaces = 0;
      let _flagged = 0;
      for(let i = 0; i < lenY; i++) {
        for(let j = 0; j < lenX; j++) {
          if( board[i][j]) { _bombs++; }
          if(game[i][j] === -1 || game[i][j] === FLAGGED) { _hiddenSpaces++; }
          if(game[i][j] === FLAGGED) { _flagged++; }
          if(game[i][j] === LOST) {
            setLost(true);
          }
        }
      }
      setBombs(_bombs);
      setHidden(_hiddenSpaces);
      setFlagged(_flagged);
    }
  },[board, game]);

  useEffect(() => {
    if((bombs === hidden) && !lost) {
      setWon(true);
    }
  }, [bombs,hidden]);

  const createNewBoard = () => {
    const newBoard: Board = [];
    const newGame: Game = [];
    for(let i = 0; i < lenY; i++) {
      newBoard.push([]);
      newGame.push([]);
      for(let j = 0; j < lenX; j++) {
        newBoard[i][j] = Math.random() >= 0.8 ? true : false;
        newGame[i][j] = -1;
      }
    }
    setBoard(newBoard);
    setGame(newGame);
  };

  const selectSquare = (x: number, y: number) => {
    const newGame: Game = [];
    for(let i = 0; i < lenY; i++) {
      newGame.push([]);
      for(let j = 0; j < lenX; j++) {
        newGame[i][j] = game[i][j];
      }
    }
      
    function countFor(y: number, x: number) {
        let count = 0;
        if(board[y-1] && board[y-1][x-1]) count++;
        if(board[y-1] && board[y-1][x]) count++;
        if(board[y-1] && board[y-1][x+1]) count++;
        if(board[y] && board[y][x-1]) count++;
        if(board[y] && board[y][x+1]) count++;
        if(board[y+1] && board[y+1][x-1]) count++;
        if(board[y+1] && board[y+1][x]) count++;
        if(board[y+1] && board[y+1][x+1]) count++;
        return count;
    }
      
    function surrCoordsFor(y: number, x: number):number[][] {
        const coords= [];
        if(newGame[y-1]!==undefined && newGame[y-1][x-1]!==undefined && newGame[y-1][x-1]===-1 ) coords.push([y-1, x-1]);
        if(newGame[y-1]!==undefined && newGame[y-1][x]!==undefined &&   newGame[y-1][x]===-1) coords.push([y-1, x]);
        if(newGame[y-1]!==undefined && newGame[y-1][x+1]!==undefined && newGame[y-1][x+1]===-1) coords.push([y-1, x+1]);
        if(newGame[y]!==undefined &&   newGame[y][x-1]!==undefined &&   newGame[y][x-1]===-1) coords.push([y, x-1]);
        if(newGame[y]!==undefined &&   newGame[y][x+1]!==undefined &&   newGame[y][x+1]===-1) coords.push([y, x+1]);
        if(newGame[y+1]!==undefined && newGame[y+1][x-1]!==undefined && newGame[y+1][x-1]===-1) coords.push([y+1, x-1]);
        if(newGame[y+1]!==undefined && newGame[y+1][x]!==undefined &&   newGame[y+1][x]===-1) coords.push([y+1, x]);
        if(newGame[y+1]!==undefined && newGame[y+1][x+1]!==undefined && newGame[y+1][x+1]===-1) coords.push([y+1, x+1]);
        return coords;
    }
      
    let toCheck:number[][] = [[y,x]];
    while(toCheck.length > 0) {
      const [yy,xx] = toCheck.shift() as [number, number];
      if (board[yy][xx]) { 
        newGame[yy][xx] = LOST;
        break;
      }
      let count = countFor(yy, xx);
      newGame[yy][xx] = count;
      if (count === 0) {
        let newcords = surrCoordsFor(yy, xx);
        toCheck = toCheck.concat(newcords);
      }
    }
    
    setGame(newGame);
  };

  const resetGame = () => {
    createNewBoard();
    setLost(false);
    setWon(false);
  };

  const toggleFlagSquare = (x: number, y: number) => {
    const newGame: Game = [];
    for(let i = 0; i < lenY; i++) {
      newGame.push([]);
      for(let j = 0; j < lenX; j++) {
        if (i === y && j === x) { 
          newGame[i][j] = (game[i][j] === FLAGGED) ? -1 : FLAGGED; 
        }
        else { newGame[i][j] = game[i][j]; }
      }
    }
    setGame(newGame);
  };

  return <>
    {loading && <Loading />}
    {!loading && narrow && 
      <div className="mine-main">
        <div className="spacer"></div>
        <div className="space-around">
          Game: {won ? 'Won!' : lost ? 'Lost!' : 'Playing'}
        </div>
        <div className="space-around">Flagged: {flagged}</div>
        <div className="spacer"></div>
      </div>
    }
    {!loading &&
      <div className="mine-main">
        <div className="spacer"></div>
        {!narrow && <div className="space-around">
          Game: {won ? 'Won!' : lost ? 'Lost!' : 'Playing'}
        </div>}
        <div className="space-around">Bombs: {bombs}</div>
        <div className="space-around">Hidden: {hidden}</div>
        {!narrow && <div className="space-around">Flagged: {flagged}</div>}
        <div className="spacer"></div>
      </div>
    }
    {!loading &&
      <div className="mine-main">
        <div className="spacer"></div>
        <GameBoard 
          game={game} 
          lost={lost} 
          won={won} 
          board={board}
          flagging={flagging}
          selectSquare={selectSquare} 
          toggleFlagSquare={toggleFlagSquare}
          clearFlagging={() =>{setFlagging(false)}}
        />
        <div className="spacer"></div>
      </div>
    }
    {!loading && !won && !lost &&
      <div className="mine-main">
        <div className="spacer"></div>
        <div className="button-wrapper">
          <div>Click to flag: </div>
          <div onClick={() => setFlagging(!flagging)} className={`flag-button ${flagging ? 'button-flagging':''}`}>⛳</div> 
        </div>
        <div className="spacer"></div>
      </div>
    }
    {!loading && !won && !lost &&
      <div className="mine-main">
        <div className="spacer"></div>
        <div>(Alt/Option)-click to flag/unflag a square</div>
        <div className="spacer"></div>
      </div>
    }
    {!loading && (won||lost) && 
      <div className="mine-main">
        <div className="spacer"></div>
        <div className="space-around"><button onClick={resetGame}>Reset</button></div>
        <div className="spacer"></div>
      </div>
    }
  </>;
}


export default Minesweeper;